import type { PostMedia, PostMediaSource } from '../../types/publishing.js';

// Publishing — the SHARED media-upload core, imported by BOTH the browser (direct
// presigned PUT) and the server (the Drive path, and the presign endpoint's own
// validation).
//
// ⚠️⚠️ IT IS SHARED SO THE TWO CANNOT DISAGREE. Since 2026-08-08 the PostMedia for
// a native upload is built IN THE BROWSER from the presign's publicUrl, where it
// used to be built on the server by mediaFromUpload. Two implementations of that
// shape is two chances for a field to drift (a missing mimeType or sizeBytes is
// invisible in the composer and only shows up at dispatch), so there is exactly
// one builder and both callers go through it.
//
// The same reasoning applies to validation: the client pre-validates so an
// unsupported file fails immediately and legibly, and the server re-validates at
// the presign endpoint because a client-side check is a courtesy, not a gate.
// One predicate, one set of messages.

// The Zernio presign contentType enum, MINUS application/pdf: PostMedia is
// image|video and the composer posts to FB/IG/GBP, none of which take a PDF. If a
// document type is ever wanted, PostMediaType has to grow first.
export const MEDIA_CONTENT_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
  'video/avi',
  'video/x-msvideo',
  'video/webm',
  'video/x-m4v',
] as const;

export type MediaContentType = (typeof MEDIA_CONTENT_TYPES)[number];

const ALLOWED = new Set<string>(MEDIA_CONTENT_TYPES);

// Zernio's documented storage ceiling for a presigned upload. It is the REAL
// limit now that bytes go browser → storage directly; the old 100MB cap existed
// only because our server buffered the file in a 1GiB container.
export const MAX_MEDIA_BYTES = 5 * 1024 * 1024 * 1024;

export const MEDIA_TYPES_MESSAGE =
  'Allowed: JPEG, PNG, WEBP, GIF images and MP4/MOV/AVI/WEBM videos.';

export type MediaValidation =
  | { ok: true; contentType: MediaContentType }
  | { ok: false; kind: 'type' | 'size' | 'empty'; message: string };

export function validateMediaFile(input: {
  mimeType: string;
  sizeBytes: number;
}): MediaValidation {
  const normalized = input.mimeType.trim().toLowerCase();
  if (!ALLOWED.has(normalized)) {
    return {
      ok: false,
      kind: 'type',
      message: `Unsupported media type "${input.mimeType || 'unknown'}". ${MEDIA_TYPES_MESSAGE}`,
    };
  }
  if (!Number.isFinite(input.sizeBytes) || input.sizeBytes <= 0) {
    return { ok: false, kind: 'empty', message: 'That file is empty.' };
  }
  if (input.sizeBytes > MAX_MEDIA_BYTES) {
    return {
      ok: false,
      kind: 'size',
      message: `File is larger than the ${Math.round(
        MAX_MEDIA_BYTES / (1024 * 1024 * 1024),
      )}GB upload limit.`,
    };
  }
  return { ok: true, contentType: normalized as MediaContentType };
}

export function mediaTypeOf(contentType: string): 'image' | 'video' {
  return contentType.toLowerCase().startsWith('video/') ? 'video' : 'image';
}

// The ONE PostMedia builder for an uploaded asset. Every field is set explicitly —
// including the nulls — so a caller can never produce a partially-populated item.
export function buildUploadedMedia(input: {
  publicUrl: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  source: PostMediaSource;
}): PostMedia {
  return {
    type: mediaTypeOf(input.contentType),
    url: input.publicUrl,
    source: input.source,
    filename: input.filename,
    mimeType: input.contentType,
    sizeBytes: input.sizeBytes,
    altText: null,
    thumbnailUrl: null,
  };
}
